import { useCallback } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type FormTimePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  label?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  preserveMessageSpace?: boolean;
  onValueChange?: (value: string) => void;
};

export const FormTimePicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  wrapperClassName,
  labelClassName,
  triggerClassName,
  contentClassName,
  descriptionClassName,
  messageClassName,
  disabled,
  preserveMessageSpace,
  onValueChange,
  ...props
}: FormTimePickerProps<TFieldValues, TName>) => {
  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const seconds = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  const formatTime = useCallback(
    (hour: string, minute: string, second: string) => {
      return `${hour}:${minute}:${second}`;
    },
    []
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // Parse time value or use default "00:00:00"
        const timeValue = field.value || "00:00:00";
        const [hour, minute, second] = timeValue.split(":");

        return (
          <FormItem
            className={wrapperClassName}
            aria-invalid={!!fieldState?.error}
          >
            {label && <FormLabel className={labelClassName}>{label}</FormLabel>}
            <FormControl>
              <div className="aero:flex aero:items-center aero:space-x-1">
                {/* Hour  */}
                <Select
                  value={hour}
                  onValueChange={(value) => {
                    const newTime = formatTime(value, minute, second);
                    field.onChange(newTime);
                    onValueChange?.(newTime);
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger
                    className={cn(
                      "aero:w-[60px] aero:h-10 aero:px-2 aero:py-3 aero:text-sm",
                      "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                      "aero:font-inter aero:font-normal aero:leading-5",
                      "aero:focus-visible:ring-[3px] aero:focus-visible:ring-ring/50",
                      triggerClassName
                    )}
                  >
                    <SelectValue placeholder="HH" />
                  </SelectTrigger>
                  <SelectContent className={contentClassName}>
                    {hours.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <span className="aero:text-2xl">:</span>

                {/* Minute  */}
                <Select
                  value={minute}
                  onValueChange={(value) => {
                    const newTime = formatTime(hour, value, second);
                    field.onChange(newTime);
                    onValueChange?.(newTime);
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger
                    className={cn(
                      "aero:w-[60px] aero:h-10 aero:px-2 aero:py-3 aero:text-sm",
                      "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                      "aero:font-inter aero:font-normal aero:leading-5",
                      "aero:focus-visible:ring-[3px] aero:focus-visible:ring-ring/50",
                      triggerClassName
                    )}
                  >
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent className={contentClassName}>
                    {minutes.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <span className="aero:text-2xl">:</span>

                {/* Second  */}
                <Select
                  value={second}
                  onValueChange={(value) => {
                    const newTime = formatTime(hour, minute, value);
                    field.onChange(newTime);
                    onValueChange?.(newTime);
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger
                    className={cn(
                      "aero:w-[60px] aero:h-10 aero:px-2 aero:py-3 aero:text-sm",
                      "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                      "aero:font-inter aero:font-normal aero:leading-5",
                      "aero:focus-visible:ring-[3px] aero:focus-visible:ring-ring/50",
                      triggerClassName
                    )}
                  >
                    <SelectValue placeholder="SS" />
                  </SelectTrigger>
                  <SelectContent className={contentClassName}>
                    {seconds.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </FormControl>
            {description && (
              <FormDescription className={descriptionClassName}>
                {description}
              </FormDescription>
            )}
            <div
              className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}
            >
              <FormMessage
                className={cn(
                  fieldState?.error
                    ? "aero:text-destructive aero:text-xs"
                    : preserveMessageSpace
                      ? "aero:invisible aero:text-xs"
                      : "aero:hidden",
                  messageClassName
                )}
              />
            </div>
          </FormItem>
        );
      }}
      {...props}
    />
  );
};
